import { create } from 'zustand'
import { api } from '@/api/client'
import type { LibraryItem, SearchResult } from '@/api/types'
import { prepareSuggestion, useAutoplay } from '@/audio/autoplay'
import { duplicateQueueItem, usePlayer, type QueueItem } from '@/audio/store'
import { useIncognito } from '@/lib/incognito'
import { describeError } from '@/features/search/describeError'

interface Notice { title: string; session: number; privacy: boolean; action?: () => void; label?: string }
export const useQueueFeedback = create<{ notice: Notice | null; pending: string[] }>(() => ({ notice: null, pending: [] }))
function notice(title: string, action?: () => void, label = 'Undo') {
  const entry = { title, action, label, session: usePlayer.getState().session, privacy: useIncognito.getState().active }
  useQueueFeedback.setState({ notice: entry })
  return entry
}
export function removeUpcoming(uid: string) {
  const player = usePlayer.getState()
  const index = player.queue.findIndex((item) => item.uid === uid)
  if (index <= player.index) return
  const item = player.queue[index]
  if (!item) return
  const before = player.queue[index + 1]?.uid, after = player.queue[index - 1]?.uid
  const session = player.session
  const privacy = useIncognito.getState().active
  player.removeAt(index)
  notice(`Removed ${item.title}`, () => {
    usePlayer.setState((state) => {
      if (state.session !== session || useIncognito.getState().active !== privacy || state.queue.some((entry) => entry.uid === uid)) return state
      const next = state.queue.findIndex((entry) => entry.uid === before)
      const previous = state.queue.findIndex((entry) => entry.uid === after)
      const position = next > state.index ? next : previous >= state.index ? previous + 1 : state.queue.length
      const queue = [...state.queue]; queue.splice(position, 0, item)
      return { queue, orderedQueue: null }
    })
  })
}

export function dismissSuggestion(key: string, title: string) {
  const restore = useAutoplay.getState().dismiss(key)
  if (restore) notice(`Dismissed ${title}`, restore)
}

type Song = QueueItem | SearchResult
export function queueSongKey(song: Song) { return 'trackId' in song ? `track:${song.trackId}` : `${song.provider}:${song.external_id}` }
function alreadyUpcoming(song: Song) {
  const { queue, index } = usePlayer.getState()
  return queue.slice(index + 1).some((item) => 'trackId' in song ? item.trackId === song.trackId
    : (song.track_id != null && item.trackId === song.track_id) || (item.provider === song.provider && item.externalId === song.external_id))
}
export function enqueueWithUndo(song: Song) {
  const key = queueSongKey(song)
  if (useQueueFeedback.getState().pending.includes(key)) return
  if (alreadyUpcoming(song)) { notice(`${song.title} is already queued`); return }
  const player = usePlayer.getState(), privacy = useIncognito.getState().active
  const controller = new AbortController()
  const sameSession = () => usePlayer.getState().session === player.session && useIncognito.getState().active === privacy
  const cancel = () => controller.abort()
  const unsubscribePlayer = usePlayer.subscribe((state) => { if (state.session !== player.session || state.intentVersion !== player.intentVersion) cancel() })
  const unsubscribePrivacy = useIncognito.subscribe(cancel)
  useQueueFeedback.setState((state) => ({ pending: [...state.pending, key] }))
  const preparingNotice = notice(`Preparing ${song.title}`, () => { cancel(); useQueueFeedback.setState({ notice: null }) }, 'Cancel')
  void (async () => {
    try {
      let source: Pick<SearchResult, 'provider' | 'external_id'>
      if ('trackId' in song) {
        source = song.provider && song.externalId ? { provider: song.provider, external_id: song.externalId }
          : (await api.get<LibraryItem>(`/library/tracks/${song.libraryItemId}`, undefined, controller.signal)).track
      } else source = song
      const ready = await prepareSuggestion(source, controller.signal)
      if (controller.signal.aborted || !sameSession()) return
      if (alreadyUpcoming(song)) { notice(`${song.title} is already queued`); return }
      const entry = duplicateQueueItem(ready)
      usePlayer.getState().enqueue([entry])
      notice(`Queued ${song.title}`, () => {
        const state = usePlayer.getState()
        const position = state.queue.findIndex((item) => item.uid === entry.uid)
        if (sameSession() && position > state.index) state.removeAt(position)
      })
    } catch (error) {
      if (!controller.signal.aborted && sameSession()) notice(describeError(error, 'Could not queue this song.'), () => enqueueWithUndo(song), 'Try again')
    } finally {
      unsubscribePlayer(); unsubscribePrivacy()
      useQueueFeedback.setState((state) => ({ pending: state.pending.filter((entry) => entry !== key),
        notice: controller.signal.aborted && state.notice === preparingNotice ? null : state.notice }))
    }
  })()
}
